import { useQuery } from '@tanstack/react-query';
import ContentLoader from 'react-content-loader';
import { useNavigate } from 'react-router-dom';

import ClassesNotFound from '../../../components/common/dashboard/ClassesNotFound';
import CourseCard from '../../../components/common/library/CourseCard';
import NavHeader from '../../../components/common/NavHeader';
import SubNavigation from '../../../components/common/SubNavigation';

import { NAVIGATE_PATH, NAVIGATION_INFO } from '../../../helpers/constants';
import { useAppDispatch } from '../../../hooks/hooks';
import { getPrivateCourses } from '../../../requests';
import { addPrivateCourse } from '../../../store/reducers/userSlice';
import { TPrivateCourse, TPrivateSeries } from '../../../types';

const PrivateCourses = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { data: { courses, series } = { courses: [], series: [] }, isLoading } =
    useQuery({
      queryKey: ['privateCourses'],
      queryFn: getPrivateCourses,
    });

  const handleCourseBooking = (privateCourse: TPrivateCourse) => {
    const { name, imageUrl } = privateCourse.coursePreview;
    dispatch(
      addPrivateCourse({
        ...privateCourse.availableTime,
        course: { name, imageUrl },
        included: true,
      }),
    );
    navigate(NAVIGATE_PATH.checkoutSummary);
  };

  const handleSeriesBooking = (seriesItem: TPrivateSeries) => {
    const unbookedItems = seriesItem.subItems.filter(
      (item) => !item.isBooked && !item.isFull,
    );

    if (!unbookedItems.length) return;

    for (const item of unbookedItems) {
      dispatch(
        addPrivateCourse({
          ...item,
          course: { name: item.name, imageUrl: item.imageUrl },
          included: true,
        }),
      );
    }

    navigate(NAVIGATE_PATH.checkoutSummary);
  };

  const isSeriesFullyBooked = (seriesItem: TPrivateSeries) => {
    return seriesItem.subItems.every((item) => item.isBooked);
  };

  const isSeriesFull = (seriesItem: TPrivateSeries) => {
    return seriesItem.subItems.every((item) => item.isBooked || item.isFull);
  };

  const getSeriesSubtitle = (seriesItem: TPrivateSeries) => {
    const count = seriesItem.subItems.length;
    const label = `${count} ${count === 1 ? 'class' : 'classes'}`;
    const withStartTime = seriesItem.subItems.filter((item) => item.startTime);
    if (!withStartTime.length) return label;
    const earliest = withStartTime.reduce((min, item) =>
      item.startTime < min.startTime ? item : min,
    );
    const startDate = new Date(earliest.startTime).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    return `${label} · starts ${startDate}`;
  };

  const isEmpty = !courses.length && !series.length;

  return (
    <>
      <NavHeader
        label="Private courses"
        className="!bg-grey-50 border border-b-1 border-grey-100"
      />
      <SubNavigation items={NAVIGATION_INFO.library.items} />
      <div className="min-h-screen main-container bg-grey-50">
        <div className="py-6 course-group-container">
          {isLoading ? (
            <ContentLoader className="private-course-card min-h-[20rem]">
              <rect x="0" y="0" rx="5" ry="0" width="100%" height="160" />
              <rect x="16" y="180" rx="5" ry="5" width="60%" height="28" />
              <rect x="16" y="228" rx="5" ry="5" width="88%" height="65" />
            </ContentLoader>
          ) : (
            isEmpty && (
              <ClassesNotFound
                title="There are no private classes to book at the moment"
                description="Please check with your company representative
                regarding available private classes with Learnit."
                buttonText="Discover public classes"
              />
            )
          )}
          {series.map((seriesItem) => (
            <div key={seriesItem.masterOppId} className="private-course-card">
              <CourseCard
                coursePreview={seriesItem.coursePreview}
                isBooked={isSeriesFullyBooked(seriesItem)}
                isFull={isSeriesFull(seriesItem)}
                subtitle={getSeriesSubtitle(seriesItem)}
                bookLabel="Enroll in series"
                onClick={
                  !isSeriesFull(seriesItem)
                    ? () => handleSeriesBooking(seriesItem)
                    : undefined
                }
              />
            </div>
          ))}
          {courses.map((course) => (
            <div key={course.availableTime.id} className="private-course-card">
              <CourseCard
                coursePreview={course.coursePreview}
                sfLearnitCourseId={course.availableTime.id}
                isBooked={course.availableTime.isBooked}
                isFull={course.availableTime.isFull}
                onClick={() => handleCourseBooking(course)}
                isWaitlisted={course.availableTime.isWaitlisted}
              />
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default PrivateCourses;
